import { useSelector } from 'react-redux';
import {
    Button,
    TextField,
    InputAdornment,
    IconButton,
    Autocomplete,
    Avatar,
    CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { BorderColor, Person, Save } from '@mui/icons-material';
import usePersonalInfo from '../hooks/usePersonalInfo';
import MuiDatePicker from '../../../../../components/FormFields/MuiDatePicker';
import { H4, H5, Body2 } from '../../../../../themes/typography';

function PersonalInfo() {
    const user = useSelector((state) => state.auth.user);
    const { cities } = useSelector((state) => state.cities);
    const { formik, avatarPreview, handleAvatarChange, loading } = usePersonalInfo();
    return (
        <div className="invoice-info-main-container">
            <form id="personal-info-form" onSubmit={formik.handleSubmit} noValidate>
                <div className="invoice-info-header">
                    <div className="invoice-info-title">
                        <span className="invoice-info-icon">
                            <Person color="secondary" sx={{ fontSize: '1.75rem' }} />
                        </span>
                        <H4 fontWeight={600}>Personal Information</H4>
                    </div>
                    <div className="invoice-info-edit-button">
                        <Button
                            variant="text"
                            type="button"
                            startIcon={
                                loading ? (
                                    <CircularProgress size={20} color="inherit" />
                                ) : formik.values.isEditable ? (
                                    <Save />
                                ) : (
                                    <BorderColor />
                                )
                            }
                            disableRipple
                            onClick={() => {
                                if (formik.values.isEditable) {
                                    formik.handleSubmit();
                                } else {
                                    formik.setFieldValue('isEditable', true);
                                }
                            }}
                            disabled={loading}
                        >
                            {formik.values.isEditable ? (loading ? 'Saving' : 'Save') : 'Edit'}
                        </Button>
                    </div>
                </div>
                <div className="invoice-info-body">
                    <div className="invoice-info-details-preview">
                        <div className="personal-info">
                            <div className="personal-photo">
                                <Avatar alt="Avatar" sx={userAvatarStyle} src={avatarPreview} />
                                {formik.values.isEditable && (
                                    <div className="personal-photo-upload-icon">
                                        <IconButton
                                            sx={photoUploadIconStyle}
                                            component="label"
                                            tabIndex={-1}
                                        >
                                            <VisuallyHiddenInput
                                                type="file"
                                                onChange={handleAvatarChange}
                                                multiple={false}
                                                accept="image/*"
                                                name="avatar"
                                            />
                                            <BorderColor
                                                color="secondary"
                                                sx={{ fontSize: '1rem' }}
                                            />
                                        </IconButton>
                                    </div>
                                )}
                            </div>
                            <div className="personal-photo-preview">
                                <H5 fontWeight={700}>{user?.name}</H5>
                                <Body2 fontWeight={400} color="var(--gray-secondary-color)">
                                    {user?.email}
                                </Body2>
                            </div>
                        </div>
                    </div>

                    <div className="invoice-info-details">
                        <div className="invoice-info-two-inputs">
                            <div className="invoice-info-input-field">
                                <TextField
                                    required
                                    fullWidth
                                    id="name"
                                    name="name"
                                    type="text"
                                    label="Full Name"
                                    disabled={!formik.values.isEditable}
                                    value={formik.values.name}
                                    onChange={(e) => formik.setFieldValue('name', e.target.value)}
                                    error={formik.touched.name && Boolean(formik.errors.name)}
                                    helperText={formik.touched.name && formik.errors.name}
                                />
                            </div>
                            <div className="invoice-info-input-field">
                                <TextField
                                    required
                                    fullWidth
                                    id="email"
                                    name="email"
                                    type="email"
                                    label="Email Address"
                                    disabled={true}
                                    value={formik.values.email}
                                    onChange={(e) => formik.setFieldValue('email', e.target.value)}
                                    error={formik.touched.email && Boolean(formik.errors.email)}
                                    helperText={formik.touched.email && formik.errors.email}
                                />
                            </div>
                        </div>
                        <div className="invoice-info-two-inputs">
                            <div className="invoice-info-input-field">
                                <TextField
                                    required
                                    fullWidth
                                    id="phone"
                                    name="phone"
                                    label="Phone Number"
                                    disabled={!formik.values.isEditable}
                                    value={formik.values.phone}
                                    onChange={(e) => formik.setFieldValue('phone', e.target.value)}
                                    error={formik.touched.phone && Boolean(formik.errors.phone)}
                                    helperText={formik.touched.phone && formik.errors.phone}
                                    slotProps={{
                                        input: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    +91
                                                </InputAdornment>
                                            ),
                                            inputMode: 'numeric',
                                            pattern: '[0-9]*',
                                            onInput: (e) => {
                                                e.target.value = e.target.value.replace(/\D/g, '');
                                                if (e.target.value.length > 10) {
                                                    e.target.value = e.target.value.slice(0, 10);
                                                }
                                            }
                                        }
                                    }}
                                />
                            </div>
                            <div className="invoice-info-input-field">
                                <MuiDatePicker
                                    fullWidth
                                    id="dob"
                                    name="dob"
                                    label="Date of Birth"
                                    disablePast={false}
                                    disableFuture={true}
                                    value={formik.values.dob}
                                    disabled={!formik.values.isEditable}
                                    onChange={(date) => formik.setFieldValue('dob', date)}
                                    error={formik.touched.dob && Boolean(formik.errors.dob)}
                                    helperText={formik.touched.dob && formik.errors.dob}
                                />
                            </div>
                        </div>
                        <div className="invoice-info-two-inputs">
                            <div className="invoice-info-input-field">
                                <Autocomplete
                                    fullWidth
                                    id="hometown"
                                    name="hometown"
                                    options={cities}
                                    disabled={!formik.values.isEditable}
                                    value={formik.values.hometown}
                                    getOptionLabel={(option) => option.name}
                                    onChange={(event, newValue) =>
                                        formik.setFieldValue('hometown', newValue)
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Hometown"
                                            error={
                                                formik.touched.hometown &&
                                                Boolean(formik.errors.hometown)
                                            }
                                            helperText={
                                                formik.touched.hometown && formik.errors.hometown
                                            }
                                        />
                                    )}
                                />
                            </div>
                            <div className="invoice-info-input-field"></div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}

export default PersonalInfo;

const photoUploadIconStyle = {
    backgroundColor: 'var(--primary-color)',
    padding: '5px',
    transition: 'all 0.2s ease',
    position: 'relative',
    '&:hover': {
        backgroundColor: 'var(--primary-color)'
    }
};

const userAvatarStyle = {
    width: '100%',
    height: '100%',
    backgroundColor: 'rgb(133, 224, 248)',
    color: 'rgb(30, 168, 202)'
};

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1
});
